import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiUser, FiCheckCircle, FiClock, FiCircle, FiTarget, FiBarChart2, FiEye, FiBell } from 'react-icons/fi';
import { Task, getTasksByProjectId, createTask, updateTask, deleteTask } from '../utils/projectService';
import { getProjectsByClientId } from '../utils/projectService';
import { MarketingTask, getMarketingTasksForClient, updateMarketingTaskStatus } from '../utils/marketingTaskService';
import { supabase } from '../utils/supabaseClient';

// Styled components
const TasksContainer = styled.div`
  color: #101828;
`;

const TasksHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TasksTitle = styled.h3`
  margin: 0;
  font-size: 18px;
`;

const TasksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
  
  th {
    text-align: left;
    padding: 12px 15px;
    background: rgba(255, 255, 255, 0.05);
    font-weight: 500;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
  }
  
  td {
    padding: 12px 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 14px;
  }
  
  tr:hover td {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.6);
`;

const Button = styled.button`
  background: rgba(31, 83, 255, 0.25);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(31, 83, 255, 0.35);
  }
`;

const ActionButton = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: none;
  cursor: pointer;
  padding: 8px;
  margin: 0 3px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(31, 83, 255, 0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(31, 83, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: rgba(31, 83, 255, 0.5);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1E2235;
  border-radius: 8px;
  padding: 25px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  color: #E0E0E0; /* Set a light base color for all text inside the modal */
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    color: #FFFFFF;
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;

  background: ${props => 
    props.$status === 'completed' ? 'rgba(52, 199, 89, 0.2)' : 
    props.$status === 'in-progress' ? 'rgba(0, 122, 255, 0.2)' : 
    'rgba(255, 149, 0, 0.2)'};
  color: ${props => 
    props.$status === 'completed' ? '#34c759' : 
    props.$status === 'in-progress' ? '#007aff' : 
    '#ff9500'};
`;

interface TasksPageProps {
  clientId: string;
}

const TasksPage: React.FC<TasksPageProps> = ({ clientId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [marketingTasks, setMarketingTasks] = useState<MarketingTask[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [marketingLoading, setMarketingLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  
  const [formData, setFormData] = useState<Partial<Task>>({
    name: '',
    description: '',
    status: 'not-started',
    assignee: '',
    project_id: '',
    due_date: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [marketingError, setMarketingError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    setLoading(true);
    setMarketingLoading(true);
    setError(null);
    setMarketingError(null);
    
    // Fetch project tasks
    try {
      // First fetch all projects for this client
      const clientProjects = await getProjectsByClientId(clientId);
      console.log('Client projects:', clientProjects);
      setProjects(clientProjects);

      // Then fetch all tasks for all projects
      const allTasks: Task[] = [];
      for (const project of clientProjects) {
        try {
          console.log(`Fetching tasks for project ${project.id} (${project.name})`);
          const projectTasks = await getTasksByProjectId(project.id);
          console.log(`Project ${project.id} tasks:`, projectTasks);
          if (projectTasks && Array.isArray(projectTasks)) {
            allTasks.push(...projectTasks.filter(task => task !== null));
          }
        } catch (taskErr) {
          console.error(`Error fetching tasks for project ${project.id}:`, taskErr);
          // Continue with other projects even if one fails
        }
      }
      console.log('All tasks:', allTasks);
      setTasks(allTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(`Failed to load tasks: ${err instanceof Error ? err.message : 'Please try again later.'}`);
    } finally {
      setLoading(false);
    }
    
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTask = () => {
    setEditingTask(null);
    // Pre-select the first project if available
    const defaultProjectId = projects.length > 0 ? projects[0].id : '';
    
    setFormData({
      name: '',
      description: '',
      status: 'not-started',
      assignee: '',
      project_id: defaultProjectId, // Pre-select the first project
      due_date: ''
    });
    setShowModal(true);
  };

  const handleEditTask = (task: Task) => {
    setFormData({
      id: task.id,
      name: task.name,
      description: task.description || '',
      notes: task.notes || '',
      status: task.status,
      assignee: task.assignee,
      project_id: task.project_id,
      due_date: task.due_date || ''
    });
    setEditingTask(task.id);
    setShowModal(true);
  };

  const handleViewTaskDetails = (task: Task) => {
    setSelectedTask(task);
    setShowTaskDetails(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter(task => task.id !== taskId));
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name) {
      alert('Task name is required');
      return;
    }
    
    // If no project is selected and there are projects available, use the first one
    let projectId = formData.project_id;
    if (!projectId && projects.length > 0) {
      projectId = projects[0].id;
      console.log(`No project selected, using first available project: ${projectId}`);
    }
    
    if (!projectId) {
      alert('No projects available. Please create a project first.');
      return;
    }
    
    try {
      if (editingTask && formData.id) {
        // Update existing task
        console.log('Updating task:', editingTask, formData);
        const updatedTask = await updateTask(editingTask, formData);
        setTasks(tasks.map(task => task.id === editingTask ? updatedTask : task));
      } else {
        // Create new task
        console.log('Creating new task with project_id:', projectId);
        const taskData = {
          name: formData.name!,
          description: formData.description,
          status: formData.status || 'not-started',
          assignee: formData.assignee || 'Unassigned',
          project_id: projectId,
          due_date: formData.due_date,
          created_by: 'admin'
        };
        
        console.log('--- DEBUG: Data being sent from handleSubmit ---', taskData);
        const newTask = await createTask(taskData);
        console.log('New task created:', newTask);
        
        // Add the new task to the tasks array
        setTasks(prevTasks => [...prevTasks, newTask]);
      }
      setShowModal(false);
      
      // Clear form data
      setFormData({
        id: '',
        name: '',
        description: '',
        notes: '',
        status: 'not-started',
        assignee: '',
        project_id: '',
        due_date: ''
      });
      
      // Refresh data to ensure we have the latest from the server
      setTimeout(() => {
        fetchData();
      }, 500); // Small delay to ensure database has time to update
    } catch (err) {
      console.error('Error saving task:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      alert(`Failed to save task: ${errorMessage}. Please check your permissions or try again later.`);
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  const renderStatusIcon = (status: string) => {
    if (status === 'completed') return <FiCheckCircle size={14} />;
    if (status === 'in-progress') return <FiClock size={14} />;
    return <FiCircle size={14} />;
  };

  const handleNotifyTaskCompletion = async (task: Task) => {
    try {
      // Get client info from the project
      const project = projects.find(p => p.id === task.project_id);
      if (!project) {
        alert('Project not found for this task');
        return;
      }

      // Get client details (prefer contact person name if available)
      const { data: clientData, error } = await supabase
        .from('clients')
        .select('name, contactname, contactemail')
        .eq('id', project.client_id)
        .single();

      if (error || !clientData) {
        console.error('Error fetching client data:', error);
        alert('Could not find client information');
        return;
      }

      // Choose a reliable client name: clients.contactname -> clients.name -> project name -> fallback
      const computedClientName = (clientData && (clientData.contactname || clientData.name)) || getProjectName(project.id) || 'Valued Client';

      // Debug logging
      console.log('Task data:', task);
      console.log('Client data:', clientData);
      console.log('Computed client name:', computedClientName);
      console.log('Sending payload:', {
        taskName: task.name,
        clientName: computedClientName,
        clientEmail: clientData.contactemail,
        actionType: 'completion'
      });

      // Call Netlify function to send notification
      const response = await fetch('/.netlify/functions/notify-task-completion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskName: task.name || 'Unnamed Task',
          clientName: computedClientName,
          clientEmail: clientData.contactemail || '',
          actionType: 'completion'
        })
      });

      if (response.ok) {
        alert(`✅ Notification sent successfully!\n\nTask: ${task.name}\nClient: ${computedClientName}\nEmails sent to client and admin.`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert(`❌ Failed to send notification: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleUpdateMarketingTaskStatus = async (taskId: number, currentStatus: string) => {
    // Define the status progression
    const statusProgression = {
      'Planned': 'In Progress',
      'In Progress': 'Completed',
      'Completed': 'Planned'
    };
    
    // Get the next status
    const nextStatus = statusProgression[currentStatus as keyof typeof statusProgression] || 'Planned';
    
    try {
      const updatedTask = await updateMarketingTaskStatus(taskId, nextStatus);
      if (updatedTask) {
        // Update the task in the local state
        setMarketingTasks(prev => 
          prev.map(task => task.id === taskId ? {...task, status: nextStatus} : task)
        );
      }
    } catch (err) {
      console.error('Error updating marketing task status:', err);
      alert('Failed to update task status. Please try again.');
    }
  };

  if (loading) return <div>Loading tasks...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <TasksContainer>
      <TasksHeader>
        <TasksTitle>Project Tasks</TasksTitle>
        <Button onClick={handleAddTask}>
          <FiPlus size={16} /> Add New Task
        </Button>
      </TasksHeader>

      {tasks.length === 0 ? (
        <EmptyState>
          <p>No project tasks found for this client.</p>
          <Button onClick={handleAddTask} style={{ margin: '10px auto', display: 'flex' }}>
            <FiPlus size={16} /> Create Your First Task
          </Button>
        </EmptyState>
      ) : (
        <TasksTable>
          <thead>
            <tr>
              <th>Task Name</th>
              <th>Project</th>
              <th>Status</th>
              <th>Assignee</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.filter(task => task !== null).map(task => (
              <tr 
                key={task.id} 
                onClick={() => handleViewTaskDetails(task)}
                style={{ cursor: 'pointer' }}
              >
                <td>{task.name}</td>
                <td>{getProjectName(task.project_id)}</td>
                <td>
                  <StatusBadge $status={task.status}>
                    {renderStatusIcon(task.status)}
                    {task.status === 'not-started' ? 'Not Started' : 
                     task.status === 'in-progress' ? 'In Progress' : 'Completed'}
                  </StatusBadge>
                </td>
                <td>{task.assignee || 'Unassigned'}</td>
                <td>{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}</td>
                <td>
                  <div style={{ display: 'flex' }}>
                    <ActionButton 
                      onClick={(e) => { e.stopPropagation(); handleViewTaskDetails(task); }} 
                      title="View task details"
                    >
                      <FiEye size={14} />
                    </ActionButton>
                    <ActionButton 
                      onClick={(e) => { e.stopPropagation(); handleNotifyTaskCompletion(task); }} 
                      title="Send completion notification"
                      style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                    >
                      <FiBell size={14} />
                    </ActionButton>
                    <ActionButton 
                      onClick={(e) => { e.stopPropagation(); handleEditTask(task); }} 
                      title="Edit task"
                    >
                      <FiEdit2 size={14} />
                    </ActionButton>
                    <ActionButton 
                      onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }} 
                      title="Delete task"
                    >
                      <FiTrash2 size={14} />
                    </ActionButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </TasksTable>
      )}


      {showTaskDetails && selectedTask && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h3>{selectedTask.name}</h3>
              <ActionButton onClick={() => setShowTaskDetails(false)}>
                ✕
              </ActionButton>
            </ModalHeader>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Project</Label>
              <div>{getProjectName(selectedTask.project_id)}</div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Status</Label>
              <StatusBadge $status={selectedTask.status}>
                {renderStatusIcon(selectedTask.status)}
                {selectedTask.status === 'not-started' ? 'Not Started' : 
                 selectedTask.status === 'in-progress' ? 'In Progress' : 'Completed'}
              </StatusBadge>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Assignee</Label>
              <div>{selectedTask.assignee || 'Unassigned'}</div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Due Date</Label>
              <div>{selectedTask.due_date ? new Date(selectedTask.due_date).toLocaleDateString() : 'No due date'}</div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Description</Label>
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.2)', 
                padding: '15px', 
                borderRadius: '4px',
                whiteSpace: 'pre-wrap'
              }}>
                {selectedTask.description || 'No description provided'}
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <Label style={{ marginBottom: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>Notes</Label>
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.2)', 
                padding: '15px', 
                borderRadius: '4px',
                whiteSpace: 'pre-wrap'
              }}>
                {selectedTask.notes || 'No notes added yet'}
              </div>
            </div>
            
            <ModalFooter>
              <Button onClick={() => handleEditTask(selectedTask)} style={{ background: 'rgba(31, 83, 255, 0.4)' }}>
                <FiEdit2 size={14} style={{ marginRight: '5px' }} /> Edit Task
              </Button>
              <Button onClick={() => setShowTaskDetails(false)} style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h3>{editingTask ? 'Edit Task' : 'Add New Task'}</h3>
              <ActionButton onClick={() => setShowModal(false)}>
                ✕
              </ActionButton>
            </ModalHeader>
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Task Name*</Label>
                <Input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleInputChange} 
                  placeholder="Enter task name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Project*</Label>
                <Select 
                  name="project_id" 
                  value={formData.project_id} 
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Description</Label>
                <TextArea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleInputChange} 
                  placeholder="Enter task description"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Notes</Label>
                <TextArea 
                  name="notes" 
                  value={formData.notes} 
                  onChange={handleInputChange} 
                  placeholder="Enter additional notes or comments"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Status</Label>
                <Select 
                  name="status" 
                  value={formData.status} 
                  onChange={handleInputChange}
                >
                  <option value="not-started">Not Started</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Assignee</Label>
                <Input 
                  type="text" 
                  name="assignee" 
                  value={formData.assignee} 
                  onChange={handleInputChange} 
                  placeholder="Who is responsible for this task?"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Due Date</Label>
                <Input 
                  type="date" 
                  name="due_date" 
                  value={formData.due_date} 
                  onChange={handleInputChange} 
                />
              </FormGroup>
              
              <ModalFooter>
                <Button type="button" onClick={() => setShowModal(false)} style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                  Cancel
                </Button>
                <Button type="submit" style={{ background: 'rgba(31, 83, 255, 0.4)' }}>
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>
      )}
    </TasksContainer>
  );
};

export default TasksPage;
